import {
  AuditTask,
  AuditResult,
  TASK_STATUS,
  RESULT_STATUS,
  UseCaseConfig,
  USE_CASE_CATEGORY,
  DATA_SOURCE_TYPE,
  COMPARISON_MODE,
  FIELD_TYPE,
  ROLE,
  PermissionsMember,
  makeReviewSampling,
  WorkspaceTask,
  WorkspaceResult,
} from "./types";

// Placeholder alias used in sample data. Thunks replace this with the
// logged-in user so isMine() matches, while preserving other reviewer IDs.
export const SAMPLE_AUDITOR_ALIAS = "sample-auditor";

// TODO: Remove all sample data once audit v2 migration is done

// Queue-level results — decision keys must match config field labels.
export const SAMPLE_RESULTS: AuditResult[] = [
  {
    id: "res-001",
    task_id: "task-sg-004",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.SUBMITTED,
    decisions: {
      "VLM verdict": { decision: "correct" },
      "VLM reasoning": { decision: "correct" },
    },
  },
  {
    id: "res-002",
    task_id: "task-ergo-002",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.SUBMITTED,
    decisions: { "Risk Score": { decision: "correct" } },
  },
  {
    id: "res-003",
    task_id: "task-ergo-002",
    auditor_id: "reviewer02",
    status: RESULT_STATUS.SUBMITTED,
    decisions: { "Risk Score": { decision: "correct" } },
  },
  {
    id: "res-004",
    task_id: "task-sv-001",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.DRAFT,
    decisions: {},
  },
];

// ----------------------------  Sample Config Data  ----------------------------

export const SAMPLE_USE_CASE_CONFIGS: UseCaseConfig[] = [
  {
    id: "uc-safety-genie",
    name: "Safety Genie - Q&A Accuracy",
    description: "Evaluate Safety Genie chatbot answers for accuracy and completeness.",
    category: USE_CASE_CATEGORY.SAFETY_GENIE,
    data_source_type: DATA_SOURCE_TYPE.TEXT_ONLY,
    comparison_mode: COMPARISON_MODE.SIMPLE,
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    claim_expiry_minutes: 60,
    status: "active",
    fields: [
      {
        id: "f-sg-verdict",
        type: FIELD_TYPE.DROPDOWN_SINGLE,
        label: "VLM verdict",
        options: ["Compliant", "Non-Compliant"],
        mandatory: true,
        help_text: "The VLM compliance verdict.",
        display_order: 0,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
        question:
          'Does the site have the "LQG and SQG Container Requirements" Visual Guide posted in the CAA?',
      },
      {
        id: "f-sg-reasoning",
        type: FIELD_TYPE.TEXT_INPUT,
        label: "VLM reasoning",
        options: [],
        mandatory: true,
        help_text: "The reasoning generated by the VLM to support its verdict.",
        display_order: 1,
        default_value: "",
        multiline: true,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
        question:
          'Does the site have the "LQG and SQG Container Requirements" Visual Guide posted in the CAA?',
      },
    ],
    checklist_items: [
      { id: "cl-sg-1", label: "Answer addresses the question", display_order: 0 },
      { id: "cl-sg-2", label: "No hallucinated facts", display_order: 1 },
      { id: "cl-sg-3", label: "Proper citation of sources", display_order: 2 },
    ],
    created_at: "2026-01-05T10:00:00Z",
    updated_at: "2026-01-05T10:00:00Z",
  },
  {
    id: "uc-safety-vision",
    name: "Safety Vision - PPE Detection",
    description: "Validate PPE detection model outputs on warehouse images.",
    category: USE_CASE_CATEGORY.SAFETY_VISION,
    data_source_type: DATA_SOURCE_TYPE.SINGLE_IMAGE_TEXT,
    comparison_mode: COMPARISON_MODE.OVERRIDE,
    inter_rater_count: 2,
    review_sampling: makeReviewSampling({
      second_pct: 10,
      second_enabled: true,
      second_reviewer: "auditor02",
    }),
    claim_expiry_minutes: 120,
    status: "active",
    fields: [
      {
        id: "f-sv-image",
        type: FIELD_TYPE.IMAGE_VIEWER,
        label: "Source Image",
        options: [],
        mandatory: true,
        help_text: "The warehouse camera image.",
        display_order: 0,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
        question: "",
      },
      {
        id: "f-sv-ppe-type",
        type: FIELD_TYPE.DROPDOWN_MULTI,
        label: "PPE Detected",
        options: ["Hard Hat", "Safety Vest", "Gloves", "Safety Glasses"],
        mandatory: true,
        help_text: "Select all PPE types detected in the image.",
        display_order: 1,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: true,
        question: "",
      },
    ],
    checklist_items: [
      { id: "cl-sv-1", label: "Bounding boxes are accurate", display_order: 0 },
      { id: "cl-sv-2", label: "All PPE items identified", display_order: 1 },
    ],
    created_at: "2026-01-06T08:00:00Z",
    updated_at: "2026-01-06T08:00:00Z",
  },
  {
    id: "uc-custom-ergo",
    name: "Custom - Ergo Risk Scoring",
    description: "Review ergonomic risk scoring results.",
    category: USE_CASE_CATEGORY.CUSTOM,
    data_source_type: DATA_SOURCE_TYPE.VIDEO_TEXT,
    comparison_mode: COMPARISON_MODE.SIMPLE,
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    claim_expiry_minutes: 240,
    status: "inactive",
    fields: [
      {
        id: "f-ergo-video",
        type: FIELD_TYPE.VIDEO_PLAYER,
        label: "Task Video",
        options: [],
        mandatory: true,
        help_text: "Video of the associate performing the task.",
        display_order: 0,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
        question: "",
      },
      {
        id: "f-ergo-score",
        type: FIELD_TYPE.NUMERIC,
        label: "Risk Score",
        options: [],
        mandatory: true,
        help_text: "Numeric risk score (1-10).",
        display_order: 1,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: 1,
        max: 10,
        step: 1,
        allow_other: false,
        question: "",
      },
    ],
    checklist_items: [{ id: "cl-ergo-1", label: "Posture evaluated correctly", display_order: 0 }],
    created_at: "2026-01-07T12:00:00Z",
    updated_at: "2026-01-07T12:00:00Z",
  },
  {
    id: "uc-hazwaste-label",
    name: "HazWaste Label Compliance",
    description: "Verify hazardous waste container labels meet regulatory requirements.",
    category: USE_CASE_CATEGORY.SAFETY_GENIE,
    data_source_type: DATA_SOURCE_TYPE.SINGLE_IMAGE_TEXT,
    comparison_mode: COMPARISON_MODE.SIMPLE,
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    claim_expiry_minutes: 120,
    status: "active",
    fields: [
      {
        id: "f-hw-verdict",
        type: FIELD_TYPE.DROPDOWN_SINGLE,
        label: "VLM verdict",
        options: ["Compliant", "Non-Compliant"],
        mandatory: true,
        help_text: "Compliant or Non-Compliant verdict from the VLM.",
        display_order: 0,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
        question:
          "Is the hazardous waste label fully legible with no smears, smudges, or obstructions?",
      },
      {
        id: "f-hw-reasoning",
        type: FIELD_TYPE.TEXT_INPUT,
        label: "VLM reasoning",
        options: [],
        mandatory: true,
        help_text: "The VLM's detailed reasoning for its verdict.",
        display_order: 1,
        default_value: "",
        multiline: true,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
        question:
          "Is the hazardous waste label fully legible with no smears, smudges, or obstructions?",
      },
    ],
    checklist_items: [],
    created_at: "2026-02-01T09:00:00Z",
    updated_at: "2026-02-01T09:00:00Z",
  },
];

// ----------------------------  Sample Workspace Data  ----------------------------

export const SAMPLE_WORKSPACE_TASKS: WorkspaceTask[] = [
  // ---  Safety Genie – Q&A Accuracy (5 tasks)  ---
  {
    id: "task-sg-001",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 2,
    created_at: "2026-01-10T08:00:00Z",
    week: "2026-W02",
    incident_description:
      "Associate reported a near-miss involving a forklift in aisle 14. The Safety Genie system generated a Q&A summary of the incident that needs accuracy review.",
    comparison_mode: "simple",
    ai_generated_values: {
      "VLM verdict": "Compliant",
      "VLM reasoning":
        "Let me analyze this label carefully.\n\nContainer Context:\n- Waste stream: Hazardous Waste\n- Waste profile: HAZ-L-CORRACIDICOR-US (starts with HAZ)\n- This means signal words ARE required.\n\nExamining the label:\nThe label header reads 'HAZARDOUS WASTE' in large text at the top.\n\nIn the body text, the Waste Description reads: 'CORROSIVE LIQUIDS ACIDIC 2 ORGANIC - Corrosive Toxic Types of products include acid-based cleaners...'\n\nAt the bottom of the label, there is a dedicated, visually prominent line reading: CORROSIVE,TOXIC\n\nThis text at the bottom (y\u22480.66 per OCR) is clearly:\n1. Separate from the body text and Waste Description\n2. Visually prominent \u2014 it appears in large, bold font, significantly larger than the surrounding body text\n3. Positioned as a dedicated signal-word field, distinct from the waste description\n4. Contains recognized hazard signal words: CORROSIVE and TOXIC\n5. Fully visible and legible\n\nThe words CORROSIVE and TOXIC also appear in the Waste Description body text, but the dedicated signal-word line at the bottom is what counts here \u2014 it is clearly a separate, standalone field with much larger text than the body text.\n\nComparing the size of 'CORROSIVE,TOXIC' to the nearby body text, it is substantially larger and more visually prominent, consistent with being greater than 0.5\" in height relative to the label size.\n\nConclusion: COMPLIANT \u2014 there is a dedicated, visually prominent signal-word line ('CORROSIVE,TOXIC') that is separate from the body text and clearly meets the requirements.",
    },
    media_urls: [],
  },
  {
    id: "task-sg-002",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 1,
    created_at: "2026-01-11T14:00:00Z",
    week: "2026-W02",
    incident_description:
      "An associate slipped on a wet floor near the break room. Safety Genie produced a root-cause summary that needs review.",
    comparison_mode: "simple",
    ai_generated_values: {
      "VLM verdict": "Non-Compliant",
      "VLM reasoning":
        "I need to carefully inspect the label for any smears, smudges, ink blots, dark marks, obstructions, or any other issues that would make the label non-compliant.\n\nLooking at the image:\n- The label is yellow with black text\n- 'HAZARDOUS WASTE' at the top is clearly readable\n- The address block (WAH000048785, Amazon.com (BF14), 21005 64th Avenue South, Kent, WA 98032) appears readable\n- 'UN1950' is clearly visible\n- Waste Profile, State of Matter, Phone number, Accumulation Date fields appear readable\n- The Waste Description text appears readable\n- 'IGNITABLE, TOXIC' at the bottom is clearly readable\n\nHowever, I notice in the top-right corner of the label, there appears to be some text or marking that the OCR picked up as 'about ICO' at coordinates [y:0.10, x:0.89-0.97]. Looking at the image more carefully, there appears to be some small text or a stamp/mark in the upper right corner of the label. This could be a sticker, stamp, or some kind of marking overlaid on the label.\n\nLooking more carefully at the top right corner of the label, I can see what appears to be a small dark mark or text overlay. The OCR detected 'about ICO' in that area, which seems unusual for a hazardous waste label. This could be a sticker or mark placed on top of the label.\n\nActually, examining the image more carefully, the top-right corner shows what appears to be a small piece of text or a label/sticker that has been placed on top of the yellow hazardous waste label. This would constitute a covering or obstruction on part of the label, making it non-compliant.\n\nHowever, looking again - the text in the top right corner ('about ICO' per OCR) appears to be very small and in the corner area. It could be a barcode label or sticker placed on the label. This would make the label non-compliant as it covers part of the label.\n\nActually, re-examining: the OCR text at y:0.10, x:0.89-0.97 shows 'about ICO' which seems to be in the upper right corner. In the image, I can see there appears to be a small sticker or label overlay in the top right corner of the yellow label. This constitutes a covering on the label, making it non-compliant.\n\nWait, let me reconsider. Looking at the image again - the top right corner does show what appears to be a small dark mark or text. But the main content of the label appears fully readable. The question is whether this constitutes a smear, obstruction, or covering.\n\nThe OCR picked up 'about ICO' in the top right corner - this seems to be some kind of additional marking or sticker on the label. If it's a sticker placed on top of the label, it would be non-compliant. But if it's just part of the label design or background, it might be compliant.\n\nLooking at the image carefully, in the top right corner of the yellow label, there appears to be a small dark rectangular element that could be a sticker or barcode label placed on top. This would make the label non-compliant as it covers part of the label surface.\n\nGiven the instruction that 'Any field value is partially or fully obscured, covered, or obstructed by marks, stains, another label, or any object' makes it non-compliant, and there appears to be something in the top right corner, I'll mark this as non-compliant.\n\nActually, looking one more time - the text in the top right corner appears to be very small and in the margin area of the label, not covering any critical field. But the instructions say even a single mark anywhere on the label makes it non-compliant. The OCR detected text 'about ICO' which seems to be a sticker or additional marking on the label.\n\nI'll mark this as non-compliant due to the apparent marking/sticker in the top right corner of the label.",
    },
    media_urls: [],
  },
  {
    id: "task-sg-003",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 3,
    created_at: "2026-01-12T06:30:00Z",
    week: "2026-W02",
    incident_description:
      "Roll cage at Dock 12 photographed during the morning safety sweep. VLM flagged no-smoking and authorized-entry signage as present, but hazardous-waste storage labeling appears missing. Review the Q&A summary.",
    comparison_mode: "simple",
    ai_generated_values: {
      "VLM verdict": "Compliant",
      "VLM reasoning":
        "The emergency exit sign is illuminated and clearly visible above the door. The pathway to the exit is clear of obstructions. The exit door hardware appears to be a push-bar type, which is correct for emergency egress. All requirements for emergency exit compliance are met.",
    },
    media_urls: [],
  },
  {
    id: "task-sg-004",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.COMPLETED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 0,
    created_at: "2026-01-08T14:00:00Z",
    week: "2026-W02",
    incident_description:
      "A floor supervisor overrode several VLM outputs during the initial pass at Sort Center Zone B. Safety Genie summarized the discrepancies. Verify the AI summary is accurate.",
    comparison_mode: "simple",
    ai_generated_values: {
      "VLM verdict": "Compliant",
      "VLM reasoning":
        "The spill kit is present and mounted on the wall in the designated area. The kit seal is intact, indicating it has not been opened or tampered with. The contents label is visible and lists the required absorbent pads, gloves, and disposal bags. The kit meets the site hazardous materials containment standard.",
    },
    media_urls: [],
  },
  {
    id: "task-sg-005",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.AVAILABLE,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 1,
    created_at: "2026-01-13T07:00:00Z",
    week: "2026-W02",
    incident_description:
      "Associate reported a box fell from an overhead rack in aisle 9 during restocking. Safety Genie generated a root-cause analysis. Confirm the AI answer is factually accurate.",
    comparison_mode: "simple",
    ai_generated_values: {
      "VLM verdict": "Non-Compliant",
      "VLM reasoning":
        "The guardrail along the mezzanine edge is only 36 inches high, which is below the OSHA-required 42-inch minimum for fall protection. Additionally, the mid-rail is missing, leaving a gap large enough for a person to fall through. This is a fall hazard violation.",
    },
    media_urls: [],
  },
  // ---  HazWaste Label Compliance (2 tasks, no checklist)  ---
  {
    id: "task-hw-001",
    use_case_id: "uc-hazwaste-label",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 1,
    created_at: "2026-02-03T09:00:00Z",
    week: "2026-W06",
    incident_description:
      "Hazardous waste label at BFI4 CAA station 3 flagged for review. The VLM detected potential signal word compliance issues.",
    comparison_mode: "simple",
    ai_generated_values: {
      "VLM verdict": "Compliant",
      "VLM reasoning":
        "The label header reads 'HAZARDOUS WASTE' in large text at the top. In the body text, the Waste Description reads: 'CORROSIVE LIQUIDS ACIDIC 2 ORGANIC - Corrosive Toxic'. At the bottom of the label, there is a dedicated, visually prominent line reading: CORROSIVE,TOXIC. This text is clearly separate from the body text and visually prominent, consistent with being greater than 0.5\" in height. Conclusion: COMPLIANT.",
    },
    media_urls: [
      { url: "/sample/hazwaste-bfi4-station3.jpg", type: "image", label: "BFI4 Station 3 Label" },
    ],
  },
  {
    id: "task-hw-002",
    use_case_id: "uc-hazwaste-label",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 2,
    created_at: "2026-02-03T10:30:00Z",
    week: "2026-W06",
    incident_description:
      "Hazardous waste label at BFI4 CAA station 7 flagged for review. The VLM detected a potential obstruction in the upper-right corner of the label.",
    comparison_mode: "simple",
    ai_generated_values: {
      "VLM verdict": "Non-Compliant",
      "VLM reasoning":
        "I need to carefully inspect the label for any smears, smudges, ink blots, dark marks, obstructions, or any other issues.\n\nLooking at the image:\n- The label is yellow with black text\n- 'HAZARDOUS WASTE' at the top is clearly readable\n- The address block (WAH000048785, Amazon.com (BF14), 21005 64th Avenue South, Kent, WA 98032) appears readable\n- 'UN1950' is clearly visible\n- 'IGNITABLE, TOXIC' at the bottom is clearly readable\n\nHowever, in the top-right corner of the label, there appears to be a small sticker or label overlay. The OCR detected 'about ICO' in that area, which seems unusual for a hazardous waste label. This constitutes a covering on the label, making it non-compliant.\n\nI'll mark this as non-compliant due to the apparent marking/sticker in the top right corner of the label.",
    },
    media_urls: [
      { url: "/sample/hazwaste-bfi4-station7.jpg", type: "image", label: "BFI4 Station 7 Label" },
    ],
  },
  // ---  Safety Vision – PPE Detection (3 tasks)  ---
  {
    id: "task-sv-001",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 1,
    created_at: "2026-01-11T10:30:00Z",
    week: "2026-W02",
    incident_description:
      "Dock loading scene captured by the CV model. Four PPE/zone detections were generated. Review each detection and mark it correct, incorrect, or partially correct.",
    comparison_mode: "override",
    ai_generated_values: {
      "PPE Detected": "Hard Hat, Safety Glasses",
    },
    human_override_values: {
      "PPE Detected": "Hard Hat, Safety Vest, Safety Glasses",
    },
    media_urls: [
      {
        url: "https://picsum.photos/seed/vision-scene/800/500",
        type: "image",
        label: "Dock Scene",
      },
    ],
  },
  {
    id: "task-sv-002",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 2,
    created_at: "2026-01-12T09:00:00Z",
    week: "2026-W02",
    incident_description:
      "Safety Vision flagged an associate near the conveyor belt without safety gloves. Verify the detection output against the camera image.",
    comparison_mode: "override",
    ai_generated_values: {
      "PPE Detected": "Safety Vest",
    },
    human_override_values: {
      "PPE Detected": "Safety Vest, Gloves",
    },
    media_urls: [
      {
        url: "https://picsum.photos/seed/conveyor4/800/500",
        type: "image",
        label: "Conveyor Camera 004",
      },
    ],
  },
  {
    id: "task-sv-003",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.AVAILABLE,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 3,
    created_at: "2026-01-13T08:15:00Z",
    week: "2026-W02",
    incident_description:
      "Warehouse aisle video clip with per-frame model detections. Step through the frames, review each detection, and adjust where the model is wrong.",
    comparison_mode: "override",
    ai_generated_values: {
      "PPE Detected": "Hard Hat",
    },
    human_override_values: {
      "PPE Detected": "Hard Hat, Safety Vest, Gloves",
    },
    media_urls: [
      {
        url: "https://picsum.photos/seed/aisle-clip/800/500",
        type: "image",
        label: "Warehouse Aisle",
      },
    ],
  },
  // ---  Custom – Ergo Risk Scoring (2 tasks)  ---
  {
    id: "task-ergo-001",
    use_case_id: "uc-custom-ergo",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 3,
    created_at: "2026-01-12T07:00:00Z",
    week: "2026-W02",
    incident_description:
      "Ergo risk model scored a repetitive lifting task at station B7 as high-risk. Review the video and model output for accuracy.",
    comparison_mode: "simple",
    ai_generated_values: {
      "Risk Score": "High",
      "Body Region": "Lower Back",
    },
    human_override_values: {},
    media_urls: [
      { url: "/sample/ergo-station-b7.mp4", type: "video", label: "Station B7 Task Video" },
    ],
  },
  {
    id: "task-ergo-002",
    use_case_id: "uc-custom-ergo",
    status: TASK_STATUS.EXPIRED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 0,
    created_at: "2026-01-01T12:00:00Z",
    week: "2026-W02",
    incident_description:
      "Ergo model flagged overhead reaching at packing station C3 as medium-risk. Validate the risk score and body region assessment.",
    comparison_mode: "simple",
    ai_generated_values: {
      "Risk Score": "Medium",
      "Body Region": "Shoulder",
    },
    human_override_values: {},
    media_urls: [
      { url: "/sample/ergo-station-c3.mp4", type: "video", label: "Station C3 Task Video" },
    ],
  },
];

export const SAMPLE_WORKSPACE_RESULTS: WorkspaceResult[] = [
  {
    id: "wres-001",
    task_id: "task-sg-001",
    auditor_id: SAMPLE_AUDITOR_ALIAS,
    status: "draft",
    decisions: {
      "VLM verdict": { decision: "correct" },
    },
    checklist_responses: {
      "cl-sg-1": "pass",
    },
    comments: "",
  },
];

// ----------------------------  Derived Queue Data  ----------------------------
// Queue-level tasks derived from the canonical workspace data so statuses stay in sync.

export const SAMPLE_TASKS: AuditTask[] = SAMPLE_WORKSPACE_TASKS.map((t) => ({
  id: t.id,
  use_case_id: t.use_case_id,
  status: t.status,
  assigned_to: t.assigned_to,
  priority: t.priority,
  created_at: t.created_at,
}));

// ----------------------------  Permissions Sample Data  ----------------------------

export const SAMPLE_MEMBERS: PermissionsMember[] = [
  { login: "admin01", name: "Sam Admin", role: ROLE.ADMIN, assigned_use_case_ids: [] },
  { login: "owner01", name: "Priya Owner", role: ROLE.OWNER, assigned_use_case_ids: [] },
  {
    login: "auditor01",
    name: "Alex Auditor",
    role: ROLE.AUDITOR,
    assigned_use_case_ids: ["uc-safety-genie", "uc-hazwaste-label", "uc-safety-vision"],
  },
  {
    login: "auditor02",
    name: "Jordan Lee",
    role: ROLE.AUDITOR,
    assigned_use_case_ids: ["uc-safety-genie", "uc-safety-vision"],
  },
  {
    login: "auditor03",
    name: "Mia Chen",
    role: ROLE.AUDITOR,
    assigned_use_case_ids: ["uc-safety-genie", "uc-hazwaste-label"],
  },
  {
    login: "auditor04",
    name: "Diego Ramos",
    role: ROLE.AUDITOR,
    assigned_use_case_ids: ["uc-safety-genie", "uc-hazwaste-label", "uc-safety-vision"],
  },
  {
    login: "auditor05",
    name: "Priyanka Nair",
    role: ROLE.AUDITOR,
    assigned_use_case_ids: ["uc-hazwaste-label"],
  },
  {
    login: "auditor06",
    name: "Tom Becker",
    role: ROLE.AUDITOR,
    assigned_use_case_ids: ["uc-safety-vision"],
  },
  {
    login: "auditor07",
    name: "Sara Okoro",
    role: ROLE.AUDITOR,
    assigned_use_case_ids: ["uc-safety-genie", "uc-hazwaste-label", "uc-safety-vision"],
  },
];
